import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type { AxiosResponse } from 'axios';
import type ApiWelcomeResponse from 'src/core/models/api/responses/ApiWelcomeResponse';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import { clamp } from 'src/core/utils/Numbers';
import { reportToCallerOrCrashlytics } from 'src/domain/stores/StoreErrorChannel';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const clampPage = (page: number, tileCount: number): number => clamp(page, 1, tileCount);

const initialState = {
	welcome: null as ApiWelcomeResponse | null,
	isLoading: false,
	hasError: false,
	featuredPage: 1,
	resortsPage: 1,
};

export const useHomeStore = create(
	combine({ ...initialState }, (set, get) => {
		const getApiWelcome = async ({
			onSuccess,
			onError,
			onFinally,
		}: {
			onSuccess?: (response: AxiosResponse<ApiWelcomeResponse>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		} = {}) => {
			if (get().welcome === null) {
				set({ isLoading: true });
			}
			set({ hasError: false });

			await ThermenRepository.getWelcome()
				.then(async (response) => {
					set({
						welcome: response.data,
						featuredPage: clampPage(get().featuredPage, response.data.data.featured.length),
						resortsPage: clampPage(get().resortsPage, response.data.data.resorts.length),
					});
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					set({ hasError: get().welcome === null });
					reportToCallerOrCrashlytics({ message: 'Fetching the home page failed.', error, onError });
				})
				.finally(() => {
					set({ isLoading: false });
					onFinally?.();
				});
		};

		const setFeaturedPage = (featuredPage: number) => set({ featuredPage });

		const setResortsPage = (resortsPage: number) => set({ resortsPage });

		const reset = () => set({ ...initialState });

		return {
			getApiWelcome,
			setFeaturedPage,
			setResortsPage,
			reset,
		};
	}),
);
